// SPDX-License-Identifier: AGPL-3.0-or-later

// LOCAL-ONLY: pull-based note sync (favorites-bookmarks-plan.md section 10). Production's gateway refuses fork events,
// so another session's note edits arrive by reloading: when a bookmarks list opens and on window focus while one is
// open (useBookmarkNoteReload), at most once per 15 s; the list's own full fetch counts as a load. The reload reads as
// many pages as the list has loaded, by the list's cursor, and changes only the notes. Nothing before the list's first
// load. A failure is logged and keeps the notes. Exclude from upstream sync.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import type {SavedMessageEntryWire} from '@app/features/messaging/models/SavedMessageEntry';
import BookmarkNotes from '@app/features/messaging/state/BookmarkNotes';
import SavedMessages from '@app/features/messaging/state/SavedMessages';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {SAVED_MESSAGES_PAGE_SIZE} from '@fluxer/constants/src/LimitConstants';

export const NOTE_RELOAD_MIN_INTERVAL_MS = 15_000;

const logger = new Logger('BookmarkNoteReload');

let lastLoad = Number.NEGATIVE_INFINITY;
let inFlight = false;

/** The list's full fetch brought every loaded note: no reload for the next 15 s. */
export function markNotesLoaded(): void {
	lastLoad = Date.now();
}

export async function reloadNotes(): Promise<void> {
	const now = Date.now();
	if (!SavedMessages.fetched || inFlight || now - lastLoad < NOTE_RELOAD_MIN_INTERVAL_MS) return;
	lastLoad = now;
	inFlight = true;
	try {
		const loaded = SavedMessages.savedMessages.length + SavedMessages.missingSavedMessages.length;
		let before: string | null = null;
		let read = 0;
		do {
			const response = await http.get<Array<SavedMessageEntryWire>>(Endpoints.USER_SAVED_MESSAGES, {
				query: {limit: SAVED_MESSAGES_PAGE_SIZE, ...(before ? {before} : {})},
			});
			const entries: Array<SavedMessageEntryWire> = response.body ?? [];
			BookmarkNotes.setFromEntries(entries.map((entry) => ({messageId: entry.message_id, note: entry.note ?? ''})));
			read += entries.length;
			if (entries.length < SAVED_MESSAGES_PAGE_SIZE) break;
			before = entries[entries.length - 1].message_id;
		} while (read < loaded);
	} catch (error) {
		logger.warn('Reloading bookmark notes failed:', error);
	} finally {
		inFlight = false;
	}
}
